import { useState } from 'react';
import { Outlet } from 'react-router-dom';

// @mui
import { styled } from '@mui/material/styles';
import { Box, IconButton, Typography } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';

import useResponsive from '../../hooks/useResponsive';

import AccountPopover from './header/AccountPopover';
import Nav from './nav';

import { APP_BAR_MOBILE, APP_BAR_DESKTOP } from './constants';

// ----------------------------------------------------------------------

const StyledRoot = styled('div')({
  display: 'flex',
  minHeight: '100%',
  overflow: 'hidden',
});

const Main = styled('div', {
  shouldForwardProp: (prop) => prop !== 'headerHeight',
})(({ headerHeight }) => ({
  flex: 1,
  overflow: 'auto',
  minHeight: '100%',
  paddingTop: headerHeight,
  paddingBottom: '30px',
}));

const Header = styled('header', {
  shouldForwardProp: (prop) => prop !== 'headerHeight',
})(({ headerHeight }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  height: headerHeight,
  backgroundColor: '#001536',
  color: '#fff',
  boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
  zIndex: 1300,

  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 1.5rem',
}));

const Footer = styled('footer')({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  padding: '0.8rem',
  backgroundColor: '#001536',
  textAlign: 'center',
  fontSize: '0.85rem',
  color: '#fff',
  zIndex: 1200,
});

const Logo = styled('img')({
  height: '75px',   // 🔥 VEĆI LOGO
  width: 'auto',
});

const HamburgerWrap = styled('div')(({ theme }) => ({
  position: 'absolute',
  left: theme.spacing(1.5),
  top: theme.spacing(1),
  zIndex: 2000,
}));

// ----------------------------------------------------------------------

export default function DashboardLayout() {
  const [open, setOpen] = useState(false);
  const isDesktop = useResponsive('up', 'lg');

  const HEADER_HEIGHT = isDesktop ? APP_BAR_DESKTOP : APP_BAR_MOBILE;

  return (
    <StyledRoot>
      <Nav
        openNav={open}
        onCloseNav={() => setOpen(false)}
        headerHeight={HEADER_HEIGHT}
      />

      <Main headerHeight={HEADER_HEIGHT}>
        <Header headerHeight={HEADER_HEIGHT}>

          {/* LEFT - hamburger */}
          {!isDesktop && (
            <HamburgerWrap>
              <IconButton onClick={() => setOpen(true)} sx={{ color: '#fff' }}>
                <MenuIcon sx={{ fontSize: 32 }} />
              </IconButton>
            </HamburgerWrap>
          )}

          {/* CENTER */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              flexGrow: 1,
              justifyContent: 'center'
            }}
          >
            <Logo src="/assets/images/logo.png" alt="Logo" />

            <Typography
              variant="h3"
              sx={{
                mt: 0.5,
                fontWeight: 700,
                letterSpacing: "1px",
                color: "#ff9800"
              }}
            >
              Smart light
            </Typography>

            <Typography
              variant="h6"
              sx={{
                opacity: 0.85,
                letterSpacing: "0.5px",
                color: "#ff9800"
              }}
            >
              BH Telecom d.d.
            </Typography>
          </Box>

          {/* RIGHT - account/logout */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              pr: 1
            }}
          >
            <AccountPopover />
          </Box>

        </Header>

        <Outlet />
      </Main>

      <Footer
        sx={{
          color: '#ff9800'
        }}
      >
        &copy; {new Date().getFullYear()} BH Telecom d.d. All rights reserved.
      </Footer>
    </StyledRoot>
  );
}